<template>
  <div>
    <button class="transition position-relative lh-1 bg-transparent p-0 border-0 text-black mb-4 mx-2 hidden listshow"
      id="userlist-show" @click="stateUserList">
      <i class="fa fa-bars text-[20px]"></i>
    </button>
  </div>
  <div class="admin-chat-container">
    <UserList :users="sortedCustomers" @selectUser="selectUser" @unRead="unRead" />
    <div class=" w-full flex flex-col justify-center items-center" :style="`height: ${computedHeight}px;`">
      <div ref="chatContent" class="chatContent flex flex-col overflow-y-auto w-full h-full mb-5" @scroll="handleScroll">
        <div
          v-for="message in messageList"
          class="message-row"
          :class="{ 'message-row--mine': isMine(message) }"
          :key="message.id"
        >
          <div
            class="message-bubble"
            :class="isMine(message) ? 'message-bubble--admin' : 'message-bubble--customer'"
          >
            <div class="message-meta">
              <span class="message-role">{{ roleLabel(message) }}</span>
              <span>{{ formatDateTime(message.timestamp) }}</span>
            </div>
            <p class="break-all">{{ message.text }}</p>
          </div>
        </div>
      </div>
      <div class="w-2/3 mt-4 flex flex-row items-center gap-x-3">
        <v-text-field ref="input" v-model="messageInput" class="w-full" variant="outlined" density="compact" placeholder="Type your message here" @keyup.enter="send" 
        rounded="1" style=""></v-text-field>
        <v-icon icon="mdi-send" color="blue" size="35px" @click="send"></v-icon>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, watch, ref, onMounted, onBeforeUnmount } from 'vue';
import { storeToRefs } from "pinia";
import UserList from './UserList.vue';
import { useAccountStore, useAlertStore } from "@/stores";
import { useChatStore } from "@/stores/chat.store";

const alertStore = useAlertStore();
const chatStore = useChatStore();
const { messages, users } = storeToRefs(chatStore);
const messageInput = ref('')
const accountStoroe = useAccountStore();
const {user} = storeToRefs(accountStoroe);
const selectedUser = ref(null);
const chatContent = ref(null);
const input = ref(null)
let chatPoller = null;
let userPoller = null;
const stateUserList = () => {
  const userlist = document.getElementById('userlist')
  if (userlist.classList.contains('userhidden')) {
    // Show the user list (left to right)
    userlist.classList.remove('userhidden');
    userlist.classList.add('uservisible');
  } else {
    // Hide the user list (right to left)
    userlist.classList.remove('uservisible');
    userlist.classList.add('userhidden');
  }
}
const messageList = computed(() => messages.value);
const sameUserId = (left, right) => left !== undefined && left !== null && right !== undefined && right !== null
  && String(left) === String(right);
const selectedCustomerId = () => selectedUser.value?.user_id ?? selectedUser.value?.id;
const isCustomerMessage = (message) => sameUserId(message.from, selectedCustomerId());
const isMine = (message) => !isCustomerMessage(message);
const roleLabel = (message) => {
  if (!isCustomerMessage(message)) {
    return sameUserId(message.from, user.value?.id) ? 'Admin (You)' : 'Admin Support';
  }
  const customerName = selectedUser.value?.name || selectedUser.value?.email;
  return customerName ? `Customer: ${customerName}` : 'Customer';
};

const refreshUsers = async () => {
  await chatStore.getUsers();
};

const selectUser = async (selecteduser) => {
  selectedUser.value = selecteduser;
  await chatStore.getMessages(selecteduser.user_id)
  await chatStore.messageRead(selecteduser.user_id)
  await refreshUsers()
  nextTick(() => {
    if (chatContent.value) chatContent.value.scrollTop = chatContent.value.scrollHeight;
  })
};

const unRead = async (user) => {
  await chatStore.markUnread(user.user_id)
}


const send = async () => {
  if(messageInput.value.trim() === '' || !selectedUser.value?.user_id)
  {
    alertStore.error("Please select user or enter message");
    return;
  }
  try {
    await chatStore.sendMessage(messageInput.value, selectedUser.value.user_id)
  } catch (error) {
    alertStore.error(error)
    return;
  }
  messageInput.value = ''
  nextTick(() => {
    input.value.focus()
    if (chatContent.value) chatContent.value.scrollTop = chatContent.value.scrollHeight;
  })
}

const offsetHeight = 250; // Adjust this value as needed

const computedHeight = ref(window.innerHeight - offsetHeight);

const handleResize = () => {
  computedHeight.value = window.innerHeight - offsetHeight;
};
onMounted(async () => {
  await refreshUsers();
  chatPoller = setInterval(async () => {
    if (selectedUser.value?.user_id) {
      await chatStore.refreshMessages(selectedUser.value.user_id);
    }
  }, 4000);
  userPoller = setInterval(async () => {
    await refreshUsers();
  }, 4000);
  window.addEventListener('resize', handleResize);
});

onBeforeUnmount(() => {
  if (chatPoller) clearInterval(chatPoller);
  if (userPoller) clearInterval(userPoller);
  window.removeEventListener('resize', handleResize);
});
let loadingMoreMessages = false;

const handleScroll = () => {
  if (chatContent.value.scrollTop === 0 && !loadingMoreMessages) {
    const previous = chatContent.value.scrollHeight
    loadingMoreMessages = true;
    chatStore.loadMoreMessages(selectedUser.value?.user_id).then(() => {
      nextTick(() => {
        loadingMoreMessages = false;
        chatContent.value.scrollTop = chatContent.value.scrollHeight - previous;
      });
    });
  }
};

const sortedCustomers = computed(() => {
  return users.value.map(customer => {
    const rawTime = customer.lastMessageTime || customer.last_message_time || customer.updatedAt || customer.createdAt;
    const lastMessageTime = rawTime ? new Date(rawTime) : null;
    return { ...customer, lastMessageTime };
  }).sort((a, b) => {
    if (!a.lastMessageTime) return 1;
    if (!b.lastMessageTime) return -1;
    return b.lastMessageTime - a.lastMessageTime;
  });
});
const formatDateTime = (timestamp) => {
  try {
  const date = typeof timestamp?.toDate === 'function' ? timestamp.toDate() : new Date(timestamp);
  if(date && !Number.isNaN(date.getTime())){
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}.${month}.${day}  ${hours}:${minutes}`;
  } else return ''
  } catch(error) {
    return ''
  }
};

watch(messages, () => {
  nextTick(() => {
    if (!loadingMoreMessages) {
      // chatContent.value.scrollIntoView({behavior: 'smooth'})
      if (chatContent.value) chatContent.value.scrollTop = chatContent.value.scrollHeight;
      if (selectedUser.value?.user_id) chatStore.messageRead(selectedUser.value.user_id)
    }
  })
}, {deep: true})
</script>

<style scoped>
.admin-chat-container {
  display: flex;
  height: 100%;
}
.message-row {
  display: flex;
  justify-content: flex-end;
  padding: 6px 14px;
}

.message-row--mine {
  justify-content: flex-start;
}

.message-bubble {
  border: 2px solid #dedede;
  background-color: #f8fafc;
  color: #111827;
  border-top-left-radius: 30px;
  border-top-right-radius: 30px;
  border-bottom-right-radius: 30px;
  padding: 10px;
  max-width: min(76%, 460px);
  min-width: 150px;
  width: fit-content;
}

.message-bubble--customer {
  border-color: #cbd5e1;
  background: #f8fafc;
  border-bottom-right-radius: 0;
  border-bottom-left-radius: 30px;
}

.message-bubble--admin {
  border-color: #93c5fd;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 30px;
  background-color: #dbeafe;
}

.message-meta {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
  color: #64748b;
  font-size: 12px;
  margin-bottom: 4px;
}

.message-role {
  color: #1d4ed8;
  font-weight: 700;
}

@media only screen and (max-width: 500px) {
  .listshow {
    display: block; 
  }
}
</style>
